import React from 'react';
import { Container, Typography, Box, Avatar, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';

// --- ICONS ---
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import GavelIcon from '@mui/icons-material/Gavel';


// --- THEME & STYLING ---
const CHARCOAL = "#18191B";
const TURQUOISE = "#008080";
const GOLD = "#FFD700";
const BURGUNDY = "#800020";
const Serif = "'Playfair Display', Georgia, serif";
const Sans = "'Montserrat', Arial, sans-serif";

// --- DATA ---
const reviews = [
    {
        name: "Lydia M.",
        text: "I never thought my living room could look this cool. The art fits my vibe perfectly. 10/10 would recommend!",
        avatar: "/reviewer1.png"
    },
    {
        name: "Ash R.",
        text: "From the moment I uploaded my photo, the process was seamless. The print quality is insane and the aesthetic is spot on.",
        avatar: "/reviewer2.png"
    },
    {
        name: "Jules S.",
        text: "Finally, a site that gets my style! My walls have never looked so alive. Fast delivery and the paper feels so premium.",
        avatar: "/reviewer3.png"
    }
];

const philosophyPoints = [
    {
        icon: <GavelIcon sx={{ fontSize: 40, color: BURGUNDY }} />,
        title: "For the Rebels",
        description: "We celebrate the bold, the different, and the unapologetically unique. Our collection is curated for those who live by their own rules."
    },
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 40, color: BURGUNDY }} />,
        title: "AI-Powered Curation",
        description: "Our smart tech isn't just code—it's your personal art critic, finding the perfect masterpiece that resonates with your space and style."
    },
    {
        icon: <HighQualityIcon sx={{ fontSize: 40, color: BURGUNDY }} />,
        title: "Uncompromising Quality",
        description: "Every print is a statement. We use thick, premium A3 paper to ensure your art not only looks good but feels legendary."
    },
];

// --- ANIMATION VARIANTS ---
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6 }
    }
};

const AboutUs = () => (
    <Box
        sx={{
            minHeight: "100vh",
            background: "linear-gradient(120deg, #18191b 60%, #232526 100%)",
            color: "#fff",
            fontFamily: Sans,
            py: { xs: 8, md: 12 },
            overflow: 'hidden',
        }}
    >
        <Container maxWidth="lg">
            {/* --- HERO SECTION --- */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                <motion.div variants={itemVariants}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: Serif,
                            color: GOLD,
                            fontWeight: 900,
                            mb: 2,
                            textAlign: "center",
                            textShadow: `0 2px 18px ${BURGUNDY}66`
                        }}
                    >
                        Where Art Meets Attitude.
                    </Typography>
                </motion.div>
                <motion.div variants={itemVariants}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: TURQUOISE,
                            fontFamily: Sans,
                            fontWeight: 400,
                            mb: 6,
                            textAlign: "center",
                            maxWidth: 700,
                            mx: 'auto'
                        }}
                    >
                        We're here for the dreamers, the rebels, and the defiant spirits. We blend edgy design with smart tech to turn your space into a stage where your personality is the headline act.
                    </Typography>
                </motion.div>
            </motion.div>

            {/* --- OUR PHILOSOPHY SECTION --- */}
            <Box sx={{ my: { xs: 8, md: 12 } }}>
                <Grid container spacing={4} justifyContent="center">
                    {philosophyPoints.map((point, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                            >
                                <Paper
                                    elevation={4}
                                    sx={{
                                        background: "#232526",
                                        borderRadius: 4,
                                        p: 4,
                                        height: '100%',
                                        textAlign: 'center',
                                        border: `1px solid ${TURQUOISE}55`,
                                        boxShadow: `0 8px 32px ${CHARCOAL}88`
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>{point.icon}</Box>
                                    <Typography variant="h5" sx={{ fontFamily: Serif, color: GOLD, fontWeight: 700, mb: 1 }}>
                                        {point.title}
                                    </Typography>
                                    <Typography sx={{ color: '#ccc', fontFamily: Sans, fontSize: 16, lineHeight: 1.6 }}>
                                        {point.description}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Box>


            {/* --- REVIEWS SECTION --- */}
            <Box sx={{ mt: { xs: 8, md: 12 } }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontFamily: Serif,
                        color: TURQUOISE,
                        fontWeight: 900,
                        mb: 6,
                        textAlign: "center",
                    }}
                >
                    What Our Tribe Says
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {reviews.map((review, idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                whileHover={{ y: -10, transition: { duration: 0.2 } }}
                                style={{ height: '100%' }}
                            >
                                <Paper
                                    elevation={6}
                                    sx={{
                                        background: `linear-gradient(145deg, #232526, ${CHARCOAL})`,
                                        borderRadius: 4,
                                        p: 4,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        height: '100%',
                                        border: `2px solid transparent`,
                                        transition: 'border-color 0.3s, box-shadow 0.3s',
                                        '&:hover': {
                                            borderColor: GOLD,
                                            boxShadow: `0 0 24px ${GOLD}33`
                                        }
                                    }}
                                >
                                    <Avatar
                                        src={review.avatar}
                                        alt={review.name}
                                        sx={{ width: 80, height: 80, mb: 2, border: `3px solid ${BURGUNDY}` }}
                                    />
                                    <Box sx={{ display: "flex", mb: 2 }}>
                                        {[...Array(5)].map((_, i) => (
                                            <StarIcon key={i} sx={{ color: GOLD }} />
                                        ))}
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: "#eee", fontStyle: 'italic', mb: 2, textAlign: "center", flexGrow: 1 }}
                                    >
                                        "{review.text}"
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ color: TURQUOISE, fontFamily: Serif, fontWeight: 700 }}
                                    >
                                        &mdash; {review.name}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Container>
    </Box>
);

export default AboutUs;
